import { useQuery } from "@apollo/client";

import { gql } from "../generated-graphql";

const FACILITY_ARCHIVED_WITH_NAME = gql(`
  query FacilityArchivedWithName($id: ID!) {
    facility(id: $id) {
      id
      archivedAt
      name
    }
  }
`);

export type FacilityReadOnlyResult = {
  isReadOnly: boolean;
  archivedAt: string | null;
  facilityName: string | null;
};

/**
 * Returns read-only state for a facility based on its archived status.
 * When archivedAt is set, the facility is server-side read-only.
 *
 * While the archived status is still loading, `isReadOnly` is true — write
 * controls stay disabled until the facility is proven editable, matching
 * useCanEditFacility. Confirmed-archived renderings (the banner) key on
 * `archivedAt`, which stays null until the query resolves, so they do not
 * flash during the loading window.
 */
export function useFacilityReadOnly(facilityId?: string): FacilityReadOnlyResult {
  const { data, loading } = useQuery(FACILITY_ARCHIVED_WITH_NAME, {
    variables: { id: facilityId ?? "" },
    skip: !facilityId,
    fetchPolicy: "cache-first",
  });

  const archivedAt = data?.facility?.archivedAt ?? null;
  const facilityName = data?.facility?.name ?? null;

  return {
    isReadOnly: loading || archivedAt !== null,
    archivedAt,
    facilityName,
  };
}
